'use client'

import {
    MEDICAL_SUPPLY_TYPE_LABELS,
    MedicalSupply,
    searchMedicalSupplies,
} from '@/lib/api/medical-supplies/medical-supplies-api'
import { useAuth } from '@/store/hooks'
import { useEffect, useRef, useState } from 'react'
import { MdClose, MdMedicalServices, MdSearch } from 'react-icons/md'

interface Props {
    value: number | null
    onChange: (supplyId: number | null, supply: MedicalSupply | null) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    className?: string
}

export function MedicalSupplySelector({
    value,
    onChange,
    label = 'Material',
    placeholder = 'Buscar material...',
    disabled = false,
    className = '',
}: Props) {
    const { user } = useAuth()
    const organizationId = user?.organizationId
    const [results, setResults] = useState<MedicalSupply[]>([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)
    const [selected, setSelected] = useState<MedicalSupply | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Carregar item já selecionado quando value muda externamente
    useEffect(() => {
        if (!value) {
            setSelected(null)
            return
        }
        if (selected?.id === value) return
        if (!organizationId) return
        searchMedicalSupplies(organizationId, '')
            .then(res => {
                if (res.success && Array.isArray(res.result)) {
                    const found = res.result.find(s => s.id === value)
                    if (found) setSelected(found)
                }
            })
            .catch(() => {})
    }, [value, organizationId, selected?.id])

    // Click outside
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setShowDropdown(false)
            }
        }
        if (showDropdown) document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [showDropdown])

    const doSearch = (term: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(async () => {
            if (!organizationId) return
            setLoading(true)
            try {
                const res = await searchMedicalSupplies(organizationId, term)
                if (res.success)
                    setResults(Array.isArray(res.result) ? res.result : [])
            } catch {
                setResults([])
            } finally {
                setLoading(false)
            }
        }, 300)
    }

    const handleSearchChange = (term: string) => {
        setSearchTerm(term)
        setShowDropdown(true)
        doSearch(term)
    }

    const handleSelect = (supply: MedicalSupply) => {
        setSelected(supply)
        onChange(supply.id, supply)
        setSearchTerm('')
        setShowDropdown(false)
    }

    const handleClear = () => {
        setSelected(null)
        onChange(null, null)
        setSearchTerm('')
    }

    return (
        <div className={`form-control w-full ${className}`} ref={containerRef}>
            <label className="label">
                <span className="label-text">{label}</span>
            </label>

            {selected ? (
                <div className="flex items-center gap-2 p-2 bg-base-200 rounded-lg">
                    <MdMedicalServices className="text-base-content/60" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                            {selected.name}
                        </p>
                        <p className="text-xs text-base-content/60">
                            {MEDICAL_SUPPLY_TYPE_LABELS[selected.type] ||
                                selected.type}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs btn-circle"
                        onClick={handleClear}
                        disabled={disabled}
                        aria-label="Limpar"
                    >
                        <MdClose />
                    </button>
                </div>
            ) : (
                <div className="relative">
                    <div className="relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
                        <input
                            type="text"
                            className="input input-bordered w-full pl-10"
                            placeholder={placeholder}
                            value={searchTerm}
                            onChange={e => handleSearchChange(e.target.value)}
                            onFocus={() => {
                                setShowDropdown(true)
                                if (!results.length) doSearch('')
                            }}
                            disabled={disabled}
                        />
                    </div>
                    {showDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                            {loading && (
                                <div className="p-3 text-center text-sm text-base-content/60">
                                    <span className="loading loading-spinner loading-xs mr-2" />
                                    Buscando...
                                </div>
                            )}
                            {!loading && results.length === 0 && (
                                <div className="p-3 text-center text-sm text-base-content/60">
                                    Nenhum material encontrado
                                </div>
                            )}
                            {!loading &&
                                results.map(s => (
                                    <button
                                        key={s.id}
                                        type="button"
                                        className="w-full text-left p-3 hover:bg-base-200 border-b border-base-300/50 last:border-b-0"
                                        onClick={() => handleSelect(s)}
                                    >
                                        <span className="font-medium text-sm">
                                            {s.name}
                                        </span>
                                        <span className="text-xs text-base-content/60 ml-2">
                                            {MEDICAL_SUPPLY_TYPE_LABELS[
                                                s.type
                                            ] || s.type}
                                        </span>
                                    </button>
                                ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
